<template>
    <!-- Botão flutuante + janela de chat da IA -->
    <div class="ai-chat-wrapper">

        <!-- Janela do chat -->
        <transition name="ai-chat-slide">
            <div v-if="aberto" class="ai-chat-window">
                <!-- Cabeçalho -->
                <div class="ai-chat-header">
                    <span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" class="me-1">
                            <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5ZM3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.58 26.58 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a2.954 2.954 0 0 1-1.504 2.573l-.157.095c-.384.23-.836.353-1.297.353H5.96c-.461 0-.913-.123-1.297-.353l-.157-.095A2.954 2.954 0 0 1 3 9.219V8.062Zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a24.767 24.767 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.01.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25.286 25.286 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.134Z"/>
                            <path d="M8 1C4.134 1 1 3.258 1 6.184c0 1.586.846 3.016 2.196 4.054v2.012a.5.5 0 0 0 .724.447L6.5 11.35a9.191 9.191 0 0 0 1.5.13c3.866 0 7-2.258 7-5.184C15 3.258 11.866 1 8 1Z"/>
                        </svg>
                        Assistente IA
                    </span>
                    <div class="d-flex gap-1 align-items-center">
                        <AjudaBotao :slug="AJUDA_SLUGS.SISTEMA_ASSISTENTE_IA" variante="popup" titulo="Ajuda: Assistente IA" />
                        <button class="ai-header-btn" title="Limpar conversa" @click="limparConversa">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                        </button>
                        <button class="ai-header-btn" @click="aberto = false">✕</button>
                    </div>
                </div>

                <!-- Mensagens -->
                <div class="ai-chat-messages" ref="msgContainer">
                    <!-- Mensagem de boas vindas -->
                    <div v-if="mensagens.length === 0" class="ai-msg ai-msg-bot">
                        <p class="mb-1">Olá! Sou seu assistente de licitações. Posso ajudar com:</p>
                        <ul class="mb-0 ps-3">
                            <li>Analisar editais (cole o texto ou a URL)</li>
                            <li>Explicar requisitos de habilitação</li>
                            <li>Tirar dúvidas sobre pregões</li>
                        </ul>
                    </div>

                    <template v-for="(msg, i) in mensagens" :key="i">
                        <div v-if="!msg.oculta" :class="['ai-msg', msg.papel === 'usuario' ? 'ai-msg-user' : 'ai-msg-bot']">
                            <span v-html="formatarMensagem(msg.texto)"></span>
                        </div>
                    </template>

                    <!-- Indicador de carregamento -->
                    <div v-if="carregando" class="ai-msg ai-msg-bot ai-typing">
                        <template v-if="statusMsg">
                            <span class="ai-status-text">{{ statusMsg }}</span>
                        </template>
                        <template v-else>
                            <span></span><span></span><span></span>
                        </template>
                    </div>
                </div>

                <!-- Campo de entrada -->
                <div class="ai-chat-input">
                    <textarea
                        v-model="textoInput"
                        class="form-control form-control-sm"
                        rows="2"
                        placeholder="Digite sua mensagem... (Enter para enviar)"
                        :disabled="carregando"
                        @keydown.enter.exact.prevent="enviarMensagem"
                    ></textarea>
                    <button class="btn btn-primary btn-sm mt-1 w-100" :disabled="carregando || !textoInput.trim()" @click="enviarMensagem">
                        <span v-if="carregando">Aguarde...</span>
                        <span v-else>Enviar</span>
                    </button>
                </div>
            </div>
        </transition>

        <!-- Botão flutuante -->
        <button class="ai-chat-fab" :class="{ 'ai-chat-fab-open': aberto }" @click="aberto = !aberto" title="Assistente IA">
            <!-- Ícone robô (fechado) -->
            <svg v-if="!aberto" xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="currentColor" viewBox="0 0 16 16">
                <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5ZM3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.58 26.58 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a2.954 2.954 0 0 1-1.504 2.573l-.157.095c-.384.23-.836.353-1.297.353H5.96c-.461 0-.913-.123-1.297-.353l-.157-.095A2.954 2.954 0 0 1 3 9.219V8.062Zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a24.767 24.767 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.01.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25.286 25.286 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.134Z"/>
                <path d="M8 1C4.134 1 1 3.258 1 6.184c0 1.586.846 3.016 2.196 4.054v2.012a.5.5 0 0 0 .724.447L6.5 11.35a9.191 9.191 0 0 0 1.5.13c3.866 0 7-2.258 7-5.184C15 3.258 11.866 1 8 1Z"/>
            </svg>
            <!-- Ícone X (aberto) -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
            </svg>
        </button>
    </div>
</template>

<script setup>
import AjudaBotao from '@/components/AjudaBotao.vue';
import { AJUDA_SLUGS } from '@/config/ajudaEngage';
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { chatIa, USO_IA_ANALISE_LICITACAO } from '@/api/ia';
import eventBus from '@/views/eventBus';

const aberto = ref(false);
const textoInput = ref('');
const carregando = ref(false);
const mensagens = ref([]);
const msgContainer = ref(null);
const statusMsg = ref(''); // mensagem de status exibida no lugar do typing (ex: "Consultando licitação...")

// Rola para o final das mensagens
const scrollFim = async () => {
    await nextTick();
    if (msgContainer.value) {
        msgContainer.value.scrollTop = msgContainer.value.scrollHeight;
    }
};

// Formata texto simples: quebras de linha e **negrito**
const formatarMensagem = (texto) => {
    if (!texto) return '';
    return texto
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
};

// Envia mensagem digitada pelo usuário
const enviarMensagem = async () => {
    const texto = textoInput.value.trim();
    if (!texto || carregando.value) return;

    mensagens.value.push({ papel: 'usuario', texto });
    textoInput.value = '';
    carregando.value = true;
    scrollFim();

    const resultado = await chatIa(mensagens.value);

    carregando.value = false;

    if (resultado.ok && resultado.data?.resposta) {
        mensagens.value.push({ papel: 'assistente', texto: resultado.data.resposta });
    } else {
        mensagens.value.push({ papel: 'assistente', texto: resultado.msg || 'Ocorreu um erro ao contatar o assistente. Tente novamente.' });
    }

    scrollFim();
};

// Envia prompt interno (não visível ao usuário) e mostra apenas a resposta
const enviarPromptInterno = async (promptInterno, statusTexto) => {
    // Limpa conversa anterior para análise ficar isolada
    mensagens.value = [];
    aberto.value = true;
    carregando.value = true;
    statusMsg.value = statusTexto || 'Consultando...';
    scrollFim();

    // Envia como mensagem de usuário para a API, mas não mostra no chat
    const mensagensEnvio = [{ papel: 'usuario', texto: promptInterno }];
    const resultado = await chatIa(mensagensEnvio, USO_IA_ANALISE_LICITACAO);

    carregando.value = false;
    statusMsg.value = '';

    if (resultado.ok && resultado.data?.resposta) {
        // Coloca no histórico: a mensagem interna fica oculta mas a resposta aparece
        mensagens.value.push({ papel: 'usuario', texto: promptInterno, oculta: true });
        mensagens.value.push({ papel: 'assistente', texto: resultado.data.resposta });
    } else {
        mensagens.value.push({ papel: 'assistente', texto: resultado.msg || 'Ocorreu um erro ao analisar a licitação. Tente novamente.' });
    }

    scrollFim();
};

// Monta prompt a partir dos dados da licitação
const onAnalisarLicitacao = (lic) => {
    const partes = [
        'Analise a seguinte licitação e faça um RESUMO completo destacando: Objeto, Valor estimado (se disponível), Prazo/Data de disputa, Modalidade, Órgão, e Pontos de atenção.',
        '',
        `Portal: ${lic.nomeportal || '-'}`,
        `Número: ${lic.numero || '-'}`,
        `Modalidade: ${lic.modalidade || '-'}`,
        `Objeto: ${lic.objeto || '-'}`,
        `Órgão: ${lic.orgao || '-'}`,
        `Cidade/Estado: ${lic.cidade || '-'} / ${lic.estado || '-'}`,
        `Data publicação: ${lic.datapublicacao || lic._pubFmt || '-'}`,
        `Data disputa: ${lic.datadisputa || lic._dispFmt || '-'}`,
        `UASG: ${lic.uasg || '-'}`,
        `Pregoeiro: ${lic.pregoeiro || '-'}`,
    ];
    if (lic.linkdet) {
        partes.push(`Link do edital/detalhes: ${lic.linkdet}`);
    }
    if (lic.endereco) {
        partes.push(`Endereço: ${lic.endereco}`);
    }
    enviarPromptInterno(partes.join('\n'), 'Consultando licitação...');
};

// Escuta evento do bus
onMounted(() => {
    eventBus.on('ai:analisar-licitacao', onAnalisarLicitacao);
});
onBeforeUnmount(() => {
    eventBus.off('ai:analisar-licitacao', onAnalisarLicitacao);
});

const limparConversa = () => {
    mensagens.value = [];
};
</script>

<style scoped>
.ai-chat-wrapper {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

/* Botão flutuante */
.ai-chat-fab {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: #fff;
    border: none;
    box-shadow: 0 4px 16px rgba(79, 70, 229, 0.45);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s, box-shadow 0.2s;
}
.ai-chat-fab:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(79, 70, 229, 0.55);
}
.ai-chat-fab-open {
    background: linear-gradient(135deg, #374151, #1f2937);
}

/* Janela do chat */
.ai-chat-window {
    width: 340px;
    max-height: 520px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
    overflow: hidden;
    border: 1px solid rgba(79, 70, 229, 0.15);
}

/* Tema escuro */
.dark .ai-chat-window {
    background: #1e2a3a;
    border-color: rgba(255,255,255,0.1);
    color: #e2e8f0;
}

.ai-chat-header {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: #fff;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 600;
}

.ai-header-btn {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.4);
    color: #fff;
    border-radius: 4px;
    padding: 2px 6px;
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
    transition: background 0.15s;
}
.ai-header-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

.ai-chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 200px;
    max-height: 340px;
}

.ai-msg {
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
    max-width: 90%;
    word-break: break-word;
}
.ai-msg-user {
    background: #4f46e5;
    color: #fff;
    align-self: flex-end;
    border-bottom-right-radius: 4px;
}
.ai-msg-bot {
    background: #f1f5f9;
    color: #1e293b;
    align-self: flex-start;
    border-bottom-left-radius: 4px;
}
.dark .ai-msg-bot {
    background: #2d3748;
    color: #e2e8f0;
}

/* Indicador "digitando" */
.ai-typing {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
}
.ai-typing span {
    width: 7px;
    height: 7px;
    background: #94a3b8;
    border-radius: 50%;
    animation: ai-bounce 1.2s infinite;
}
.ai-typing span:nth-child(2) { animation-delay: 0.2s; }
.ai-typing span:nth-child(3) { animation-delay: 0.4s; }
.ai-status-text {
    font-size: 12px;
    color: #64748b;
    animation: ai-pulse 1.5s infinite;
}
.dark .ai-status-text {
    color: #94a3b8;
}
@keyframes ai-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}
@keyframes ai-bounce {
    0%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-6px); }
}

.ai-chat-input {
    padding: 10px 12px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
}
.dark .ai-chat-input {
    background: #1a2332;
    border-color: rgba(255,255,255,0.08);
}
.ai-chat-input textarea {
    resize: none;
    font-size: 13px;
}

/* Animação de abrir/fechar */
.ai-chat-slide-enter-active,
.ai-chat-slide-leave-active {
    transition: all 0.25s ease;
}
.ai-chat-slide-enter-from,
.ai-chat-slide-leave-to {
    opacity: 0;
    transform: translateY(16px) scale(0.97);
}

/* Botão xs não existe no Bootstrap, criar */
.btn-xs {
    padding: 2px 6px;
    font-size: 11px;
}
</style>
